import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "../lib/api";
import { TEST_IDS } from "../constants/testIds";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Download,
  UserPlus,
  MoreVertical,
  UserMinus,
  UserCheck,
  Trash2,
  Crown,
  Mail,
  Send,
  Copy,
  Check,
  Sparkles,
  Loader2,
  Info,
  Bell,
  Lock,
  LockOpen,
  ShieldAlert,
  Eye,
  EyeOff,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { useAuthStore } from "../store/authStore";
import PendingInvitations from "../components/users/PendingInvitations";
import { UserAvatar } from "../components/users/UserAvatar";

export default function Users() {
  const { t } = useTranslation();
  const { user: me, tenant: myTenant } = useAuthStore();
  // Helpers: ultra_admin is a superset of super_admin for all
  // permission gates. `isElevated` returns true for both so the
  // existing role-guarded UI (invitations panel, kebab menu, etc.)
  // stays visible to the ultra admin.
  const isElevatedRole = (r) => r === "super_admin" || r === "ultra_admin";
  const isUltra = me?.role === "ultra_admin";
  const roleLabel = (r) => {
    if (r === "ultra_admin") return t("users.role_ultra_admin");
    if (r === "super_admin") return t("users.role_super_admin");
    return t("users.role_user");
  };
  const roleBadgeClass = (r) => {
    if (r === "ultra_admin")
      return "bg-gradient-to-r from-violet-600 to-indigo-600 text-white";
    if (r === "super_admin") return "bg-zinc-900 text-white";
    return "bg-zinc-100 text-zinc-700";
  };
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(_initialInviteForm());
  const [inviteResult, setInviteResult] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [copiedToken, setCopiedToken] = useState(false);
  const [invitesRefreshKey, setInvitesRefreshKey] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightId = searchParams.get("highlight");
  const [glowUserId, setGlowUserId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // user pending hard-delete
  // "Hatırlat" (nudge) modal state — super admin sends a warm "we miss
  // you" mail to teammates who've drifted. Subject + body are prefilled
  // from a template but fully editable before sending.
  const [remindTarget, setRemindTarget] = useState(null);
  const [remindSubject, setRemindSubject] = useState("");
  const [remindMessage, setRemindMessage] = useState("");
  const [sendingReminder, setSendingReminder] = useState(false);
  const rowRefs = useRef({});

  const load = async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  };
  useEffect(() => {
    load();
  }, []);

  // Scroll-to + glow when navigating in from a "Profili Görüntüle" click.
  // Runs after users are loaded so the row actually exists in the DOM.
  useEffect(() => {
    if (!highlightId || users.length === 0) return;
    const el = rowRefs.current[highlightId];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setGlowUserId(highlightId);
      const timer = setTimeout(() => {
        setGlowUserId(null);
        // Strip the param so a manual refresh doesn't re-trigger the glow.
        const next = new URLSearchParams(searchParams);
        next.delete("highlight");
        setSearchParams(next, { replace: true });
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [highlightId, users, searchParams, setSearchParams]);

  const openInviteDialog = () => {
    setForm(_initialInviteForm());
    setInviteResult(null);
    setCopiedToken(false);
    setOpen(true);
  };

  const closeInviteDialog = (o) => {
    if (o === false || o === undefined) {
      setOpen(false);
      setInviteResult(null);
      setCopiedToken(false);
      setForm(_initialInviteForm());
    } else {
      setOpen(true);
    }
  };

  const invite = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.email.includes("@")) {
      toast.error("Geçerli bir e-posta gir");
      return;
    }
    setInviting(true);
    try {
      const { data } = await api.post("/users/invite", {
        email: form.email.trim(),
        name: form.name.trim() || null,
        role: form.role,
        can_create_projects:
          form.role === "super_admin" ? true : form.can_create_projects,
        can_manage_contacts:
          form.role === "super_admin" ? true : form.can_manage_contacts,
      });
      setInviteResult(data);
      toast.success(t("users.invite_sent"));
      load();
      setInvitesRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setInviting(false);
    }
  };

  const inviteAnother = () => {
    setForm(_initialInviteForm());
    setInviteResult(null);
    setCopiedToken(false);
  };

  const copyInviteToken = async () => {
    // Copy the URL-safe TOKEN — not the internal invitation_id. The
    // /accept-invite page's `POST /users/accept-invitation` endpoint
    // validates on the `token` field; the short id is only for admin
    // reference. Copying the wrong field caused "Invalid invitation".
    const token = inviteResult?.token || inviteResult?.invitation_id;
    if (!token) return;
    try {
      await navigator.clipboard.writeText(token);
      setCopiedToken(true);
      setTimeout(() => setCopiedToken(false), 1500);
    } catch {
      /* no-op */
    }
  };

  const doDeleteUser = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/users/${deleteTarget.id}`);
      toast.success(`${deleteTarget.name} silindi`);
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  // Ultra admin — demote a super_admin (or any elevated non-ultra role)
  // to a plain user. Backend enforces the guardrails.
  const demoteToUser = async (u) => {
    if (!window.confirm(
      `${u.name} artık normal Kullanıcı olacak. Süper admin yetkilerini kaybedecek. Emin misin?`,
    )) return;
    try {
      await api.post(`/users/${u.id}/demote-to-user`);
      toast.success(`${u.name} artık Kullanıcı`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  // Ultra admin — toggle the "Deal tutarlarını görebilir" permission
  // per-user WITHOUT promoting them to super_admin. Backend strips
  // this field from any non-ultra caller's PATCH so it can't leak.
  const toggleCanSeeDealAmounts = async (u) => {
    try {
      const { data } = await api.patch(`/users/${u.id}`, {
        can_see_deal_amounts: !u.can_see_deal_amounts,
      });
      toast.success(
        data.can_see_deal_amounts
          ? `${u.name} artık Satış Pipeline tutarlarını görebilir`
          : `${u.name} artık tutarları göremiyor (maskeli)`,
      );
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  // "Hatırlat" flow — prefill a warm template that references the
  // recipient by name and mentions the ekip's activity. User can tweak
  // freely before sending.
  const openRemind = (u) => {
    const tenantName = myTenant?.name || "Ekibimiz";
    const displayName = u.name || (u.email || "").split("@")[0];
    setRemindTarget(u);
    setRemindSubject(`Merhaba ${displayName}, ${tenantName} sizi Serpix'te bekliyor 👋`);
    setRemindMessage(
      `Merhaba ${displayName},\n\n` +
        `Serpix'te sizi bir süredir göremedik. ${tenantName} ekibi devam eden görevlerde katkınızı bekliyor.\n\n` +
        "Kaldığınız yerden devam etmek için aşağıdaki butona tıklayın — " +
        "sizi bekleyen görevler, mention'lar ve son güncellemeler orada.\n\n" +
        `Görüşmek üzere,\n${me?.name || "Ekip yöneticisi"}\n${tenantName}`,
    );
  };

  const sendReminder = async () => {
    if (!remindTarget) return;
    if (!remindSubject.trim() || !remindMessage.trim()) {
      toast.error("Konu ve mesaj boş olamaz");
      return;
    }
    setSendingReminder(true);
    try {
      const { data } = await api.post(`/users/${remindTarget.id}/remind`, {
        subject: remindSubject.trim(),
        message: remindMessage.trim(),
      });
      if (data?.delivered) {
        toast.success(`Hatırlatma ${remindTarget.name}'e gönderildi`);
      } else {
        toast.success(
          `Hatırlatma sırada — SendGrid şu an devrede değil, outbox'a alındı`,
        );
      }
      setRemindTarget(null);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Hatırlatma gönderilemedi",
      );
    } finally {
      setSendingReminder(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      toast.success("Updated");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleCanCreateProjects = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, {
        can_create_projects: !u.can_create_projects,
      });
      toast.success(
        !u.can_create_projects
          ? `${u.name} artık proje oluşturabilir`
          : `${u.name} için proje oluşturma kapatıldı`,
      );
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleCanManageContacts = async (u) => {
    // `can_manage_contacts` defaults to true — coerce undefined for the
    // legacy users seeded before this feature was added.
    const current = u.can_manage_contacts !== false;
    try {
      await api.patch(`/users/${u.id}`, {
        can_manage_contacts: !current,
      });
      toast.success(
        !current
          ? `${u.name} artık kontak yönetebilir`
          : `${u.name} için kontak yönetimi kapatıldı`,
      );
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const transfer = async (u) => {
    // Ultra admin: promote directly via role PATCH — nobody gets demoted.
    // Regular super_admin keeps the legacy "transfer" semantic (they hand
    // over their own super admin seat).
    try {
      if (isUltra) {
        await api.patch(`/users/${u.id}`, { role: "super_admin" });
        toast.success(`${u.name} artık Süper Yönetici`);
      } else {
        await api.post(`/users/${u.id}/transfer-super-admin`);
        toast.success("Transferred");
      }
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const exportCsv = () => {
    const rows = [
      ["id", "name", "email", "role", "active", "timezone"],
      ...users.map((u) => [u.id, u.name, u.email, u.role, u.active, u.timezone]),
    ];
    const csv = rows
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "serpix-users.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-500">
            {t("brand")}
          </div>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight text-zinc-950 sm:text-4xl">
            {t("users.title")}
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isUltra && <SystemLockControl />}
          <Button
            variant="outline"
            className="gap-2 border-zinc-300"
            onClick={exportCsv}
          >
            <Download className="h-4 w-4" />
            {t("users.export_csv")}
          </Button>
          <Dialog open={open} onOpenChange={closeInviteDialog}>
            <DialogTrigger asChild>
              <Button
                data-testid={TEST_IDS.inviteUserBtn}
                onClick={openInviteDialog}
                className="gap-2 bg-zinc-900 text-white hover:bg-zinc-800"
              >
                <UserPlus className="h-4 w-4" />
                {t("users.invite")}
              </Button>
            </DialogTrigger>
            <DialogContent
              className="max-h-[90vh] max-w-lg overflow-y-auto"
              data-testid="user-invite-dialog"
            >
              {!inviteResult ? (
                <>
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <UserPlus className="h-5 w-5 text-violet-600" />
                      Kullanıcı davet et
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={invite} className="space-y-4">
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
                        E-posta *
                      </Label>
                      <Input
                        type="email"
                        required
                        value={form.email}
                        onChange={(e) =>
                          setForm({ ...form, email: e.target.value })
                        }
                        placeholder="ornek@sirket.com"
                        data-testid={TEST_IDS.inviteEmail}
                        className="mt-1"
                        autoFocus
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
                        Ad Soyad{" "}
                        <span className="text-zinc-400 normal-case">
                          (opsiyonel)
                        </span>
                      </Label>
                      <Input
                        value={form.name}
                        onChange={(e) =>
                          setForm({ ...form, name: e.target.value })
                        }
                        placeholder="Ali Veli"
                        data-testid={TEST_IDS.inviteName}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
                        Rol
                      </Label>
                      <Select
                        value={form.role}
                        onValueChange={(v) => setForm({ ...form, role: v })}
                      >
                        <SelectTrigger
                          className="mt-1"
                          data-testid={TEST_IDS.inviteRole}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="user">
                            {t("users.role_user")}
                          </SelectItem>
                          <SelectItem value="super_admin">
                            {t("users.role_super_admin")}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Permissions — only meaningful for role=user. Super
                        admins always have full permissions. */}
                    {form.role === "user" && (
                      <div className="rounded-lg border border-zinc-200 bg-zinc-50/50 p-3">
                        <div className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                          <Sparkles className="h-3 w-3" />
                          Yetkiler
                        </div>
                        <div className="space-y-2">
                          <label className="flex cursor-pointer items-start gap-2.5">
                            <Checkbox
                              checked={form.can_create_projects}
                              onCheckedChange={(v) =>
                                setForm({
                                  ...form,
                                  can_create_projects: !!v,
                                })
                              }
                              data-testid="invite-perm-create-projects"
                              className="mt-0.5"
                            />
                            <span className="text-xs text-zinc-800">
                              <b>Proje oluşturabilir</b>
                              <span className="ml-1 text-zinc-500">
                                — kullanıcı kendi projelerini kurabilir
                              </span>
                            </span>
                          </label>
                          <label className="flex cursor-pointer items-start gap-2.5">
                            <Checkbox
                              checked={form.can_manage_contacts}
                              onCheckedChange={(v) =>
                                setForm({
                                  ...form,
                                  can_manage_contacts: !!v,
                                })
                              }
                              data-testid="invite-perm-manage-contacts"
                              className="mt-0.5"
                            />
                            <span className="text-xs text-zinc-800">
                              <b>Kontak yönetebilir</b>
                              <span className="ml-1 text-zinc-500">
                                — CRM&apos;de firma/kişi ekleyip düzenleyebilir
                              </span>
                            </span>
                          </label>
                        </div>
                      </div>
                    )}
                    {form.role === "super_admin" && (
                      <div className="flex items-start gap-2 rounded-md bg-violet-50 p-3 text-xs text-violet-900">
                        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <span>
                          Süper adminlerin tüm yetkileri her zaman açıktır ve
                          değiştirilemez.
                        </span>
                      </div>
                    )}
                    <div className="flex items-start gap-2 rounded-md bg-blue-50 p-3 text-xs text-blue-900">
                      <Mail className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>
                        Davet e-postası SendGrid üzerinden anlık olarak
                        iletilir. Davet <b>30 gün</b> geçerlidir.
                      </span>
                    </div>
                    <Button
                      type="submit"
                      disabled={inviting}
                      data-testid={TEST_IDS.inviteSubmit}
                      className="w-full gap-1.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:from-violet-700 hover:to-indigo-700"
                    >
                      {inviting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Gönderiliyor...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4" />
                          Daveti gönder
                        </>
                      )}
                    </Button>
                  </form>
                </>
              ) : (
                // Success state — mirrors TenantInviteDialog UX.
                <>
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-emerald-600" />
                      Davet gönderildi!
                    </DialogTitle>
                  </DialogHeader>
                  <div
                    className="space-y-3"
                    data-testid="user-invite-result"
                  >
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-800">
                        Davet başarıyla oluşturuldu
                      </div>
                      <div className="mt-1 text-sm font-medium text-emerald-900">
                        Alıcı: {form.email}
                      </div>
                      <div className="mt-0.5 text-[11px] text-emerald-700">
                        Rol:{" "}
                        {form.role === "super_admin"
                          ? "Süper Admin"
                          : "Kullanıcı"}{" "}
                        • 30 gün geçerli
                      </div>
                    </div>
                    <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3">
                      <div className="mb-1 flex items-center justify-between text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                        <span>Davet Tokenı</span>
                        <button
                          type="button"
                          onClick={copyInviteToken}
                          data-testid="user-invite-copy-token"
                          className="inline-flex items-center gap-1 rounded-md bg-white px-2 py-0.5 text-[10px] font-medium text-violet-700 ring-1 ring-violet-200 hover:bg-violet-50"
                        >
                          {copiedToken ? (
                            <>
                              <Check className="h-3 w-3" /> Kopyalandı
                            </>
                          ) : (
                            <>
                              <Copy className="h-3 w-3" /> Kopyala
                            </>
                          )}
                        </button>
                      </div>
                      <div className="break-all font-mono text-[11px] text-zinc-800">
                        {inviteResult.token || inviteResult.invitation_id}
                      </div>
                      <div className="mt-2 text-[10px] text-zinc-500">
                        Bu tokenı paylaş — davet edilen kişi{" "}
                        <b>/accept-invite</b> sayfasına yapıştırıp hesabını oluşturur.
                      </div>
                    </div>
                    <div className="flex items-start gap-2 rounded-lg bg-violet-50 p-3 text-xs text-violet-900">
                      <Mail className="mt-0.5 h-4 w-4 shrink-0" />
                      <div>
                        Kullanıcı e-postasındaki linke tıklayarak hesabını
                        oluşturabilir. Panel&apos;de <b>Bekleyen davetler</b>{" "}
                        bölümünden ilerlemesini takip edebilirsin.
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={inviteAnother}
                        data-testid="user-invite-another-btn"
                        className="flex-1"
                      >
                        <UserPlus className="mr-1.5 h-4 w-4" />
                        Başkasını davet et
                      </Button>
                      <Button
                        type="button"
                        onClick={() => closeInviteDialog(false)}
                        data-testid="user-invite-done-btn"
                        className="flex-1 bg-zinc-900 text-white hover:bg-zinc-800"
                      >
                        Tamam
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Pending invitations panel (elevated roles only) */}
      {isElevatedRole(me?.role) && (
        <PendingInvitations refreshKey={invitesRefreshKey} />
      )}

      {/* Mobile — stacked cards */}
      <div className="space-y-3 md:hidden">
        {users.map((u) => (
          <div
            key={u.id}
            ref={(el) => {
              rowRefs.current[u.id] = el;
            }}
            data-testid={TEST_IDS.userRow(u.id)}
            className={`rounded-xl border bg-white p-4 ${
              glowUserId === u.id
                ? "border-violet-400 serpix-row-glow"
                : "border-zinc-200"
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex min-w-0 items-center gap-3">
                <UserAvatar
                  name={u.name}
                  email={u.email}
                  photoUrl={u.photo_url}
                  size="md"
                />
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-zinc-900">
                    {u.name}
                  </div>
                  <div className="mt-0.5 truncate text-xs text-zinc-500">
                    {u.email}
                  </div>
                </div>
              </div>
              <span className={`flex-none rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${roleBadgeClass(u.role)}`}>
                {roleLabel(u.role)}
              </span>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 text-xs text-zinc-600">
              <div className="flex items-center justify-between rounded-lg bg-zinc-50 px-3 py-2">
                <span>{t("users.active")}</span>
                <Switch
                  checked={u.active}
                  onCheckedChange={() => toggleActive(u)}
                  disabled={u.id === me?.id}
                  data-testid={TEST_IDS.toggleUserActive(u.id)}
                />
              </div>
              <div className="flex items-center justify-between rounded-lg bg-zinc-50 px-3 py-2">
                <span>Proje +</span>
                {u.role === "super_admin" ? (
                  <span className="text-[10px] text-zinc-400">daima</span>
                ) : (
                  <Switch
                    checked={!!u.can_create_projects}
                    onCheckedChange={() => toggleCanCreateProjects(u)}
                    disabled={!u.active}
                    data-testid={`toggle-can-create-projects-${u.id}`}
                  />
                )}
              </div>
              <div className="flex items-center justify-between rounded-lg bg-zinc-50 px-3 py-2">
                <span>Kontak +</span>
                {u.role === "super_admin" ? (
                  <span className="text-[10px] text-zinc-400">daima</span>
                ) : (
                  <Switch
                    checked={u.can_manage_contacts !== false}
                    onCheckedChange={() => toggleCanManageContacts(u)}
                    disabled={!u.active}
                    data-testid={`toggle-can-manage-contacts-${u.id}`}
                  />
                )}
              </div>
            </div>
            {u.role !== "super_admin" && u.id !== me?.id && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={TEST_IDS.transferSuperAdmin(u.id)}
                    className="mt-3 w-full border-zinc-300 text-xs"
                  >
                    {t("users.transfer")}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t("users.transfer")}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {isUltra
                        ? `${u.name} Süper Yönetici yetkilerine yükseltilecek. Senin rolün değişmez.`
                        : `${u.name} will become Super Admin and you will become a regular user.`}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t("common.no")}</AlertDialogCancel>
                    <AlertDialogAction onClick={() => transfer(u)}>
                      {t("common.yes")}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        ))}
      </div>

      {/* Desktop — table */}
      <div className="hidden overflow-hidden rounded-lg border border-zinc-200 bg-white md:block">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="px-6 py-3 text-left font-medium">{t("users.name")}</th>
              <th className="px-6 py-3 text-left font-medium">{t("users.email")}</th>
              <th className="px-6 py-3 text-left font-medium">{t("users.role")}</th>
              <th className="px-6 py-3 text-left font-medium">{t("users.active")}</th>
              <th className="px-6 py-3 text-left font-medium">Proje oluşturabilir</th>
              <th className="px-6 py-3 text-left font-medium">Kontak yönetebilir</th>
              <th className="px-6 py-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {users.map((u) => (
              <tr
                key={u.id}
                ref={(el) => {
                  rowRefs.current[u.id] = el;
                }}
                data-testid={TEST_IDS.userRow(u.id)}
                className={`transition-colors ${
                  glowUserId === u.id
                    ? "serpix-row-glow"
                    : "hover:bg-zinc-50"
                }`}
              >
                <td className="px-6 py-3 font-medium text-zinc-900">
                  <div className="flex items-center gap-3">
                    <UserAvatar
                      name={u.name}
                      email={u.email}
                      photoUrl={u.photo_url}
                      size="sm"
                    />
                    <span>{u.name}</span>
                  </div>
                </td>
                <td className="px-6 py-3 text-zinc-600">{u.email}</td>
                <td className="px-6 py-3">
                  <span className={`rounded-full px-2 py-0.5 text-xs ${roleBadgeClass(u.role)}`}>
                    {roleLabel(u.role)}
                  </span>
                </td>
                <td className="px-6 py-3">
                  <Switch
                    checked={u.active}
                    onCheckedChange={() => toggleActive(u)}
                    disabled={u.id === me?.id}
                    data-testid={TEST_IDS.toggleUserActive(u.id)}
                  />
                </td>
                <td className="px-6 py-3">
                  {isElevatedRole(u.role) ? (
                    <span className="text-xs text-zinc-400">— her zaman —</span>
                  ) : (
                    <Switch
                      checked={!!u.can_create_projects}
                      onCheckedChange={() => toggleCanCreateProjects(u)}
                      disabled={!u.active}
                      data-testid={`toggle-can-create-projects-${u.id}`}
                    />
                  )}
                </td>
                <td className="px-6 py-3">
                  {isElevatedRole(u.role) ? (
                    <span className="text-xs text-zinc-400">— her zaman —</span>
                  ) : (
                    <Switch
                      checked={u.can_manage_contacts !== false}
                      onCheckedChange={() => toggleCanManageContacts(u)}
                      disabled={!u.active}
                      data-testid={`toggle-can-manage-contacts-${u.id}`}
                    />
                  )}
                </td>
                <td className="px-6 py-3 text-right">
                  {u.id === me?.id ? (
                    <span className="text-[11px] text-zinc-400">Sen</span>
                  ) : (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="sm"
                          variant="ghost"
                          data-testid={`user-actions-${u.id}`}
                          className="h-8 w-8 p-0"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuItem
                          onClick={() => toggleActive(u)}
                          data-testid={`user-action-toggle-active-${u.id}`}
                        >
                          {u.active ? (
                            <>
                              <UserMinus className="mr-2 h-4 w-4" />
                              Pasife al
                            </>
                          ) : (
                            <>
                              <UserCheck className="mr-2 h-4 w-4" />
                              Aktive et
                            </>
                          )}
                        </DropdownMenuItem>
                        {u.active && (
                          <DropdownMenuItem
                            onClick={() => openRemind(u)}
                            data-testid={`user-action-remind-${u.id}`}
                          >
                            <Bell className="mr-2 h-4 w-4" />
                            Hatırlat
                          </DropdownMenuItem>
                        )}
                        {/* Ultra admin — per-user "can see deal amounts"
                            toggle. Only relevant for regular users; super
                            & ultra admins always see amounts. */}
                        {isUltra && u.role === "user" && (
                          <DropdownMenuItem
                            onClick={() => toggleCanSeeDealAmounts(u)}
                            data-testid={`user-action-toggle-see-amounts-${u.id}`}
                          >
                            {u.can_see_deal_amounts ? (
                              <>
                                <EyeOff className="mr-2 h-4 w-4" />
                                Tutarları gizle
                              </>
                            ) : (
                              <>
                                <Eye className="mr-2 h-4 w-4" />
                                Tutarları göster
                              </>
                            )}
                          </DropdownMenuItem>
                        )}
                        {u.role !== "super_admin" && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <DropdownMenuItem
                                onSelect={(e) => e.preventDefault()}
                                data-testid={TEST_IDS.transferSuperAdmin(u.id)}
                              >
                                <Crown className="mr-2 h-4 w-4" />
                                Süper admin yap
                              </DropdownMenuItem>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  {u.name} artık Süper Admin olacak
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  {isUltra
                                    ? `${u.name} Süper Yönetici yetkilerine yükseltilecek. Senin rolün değişmez. İstersen daha sonra tekrar Kullanıcıya düşürebilirsin.`
                                    : `${u.name} yeni Süper Admin olacak ve sen normal kullanıcıya düşeceksin. Bu işlem geri alınamaz.`}
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Vazgeç</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => transfer(u)}
                                  className="bg-violet-600 hover:bg-violet-700"
                                >
                                  {isUltra ? "Yükselt" : "Devret"}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                        {u.role !== "super_admin" && u.role !== "ultra_admin" && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => setDeleteTarget(u)}
                              data-testid={`user-action-delete-${u.id}`}
                              className="text-red-600 focus:bg-red-50 focus:text-red-700"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Sil
                            </DropdownMenuItem>
                          </>
                        )}
                        {/* Ultra admin: relaxed guardrails for super_admin
                            targets — demote AND hard-delete are unlocked. */}
                        {isUltra && u.role === "super_admin" && u.id !== me?.id && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => demoteToUser(u)}
                              data-testid={`user-action-demote-${u.id}`}
                              className="text-amber-700 focus:bg-amber-50 focus:text-amber-800"
                            >
                              <UserMinus className="mr-2 h-4 w-4" />
                              Kullanıcıya düşür
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => setDeleteTarget(u)}
                              data-testid={`user-action-delete-${u.id}`}
                              className="text-red-600 focus:bg-red-50 focus:text-red-700"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Süper admini sil
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Hard-delete confirmation */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      >
        <AlertDialogContent data-testid="user-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Kullanıcıyı silmek istediğine emin misin?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-zinc-600">
                <p>
                  <b>{deleteTarget?.name}</b> ({deleteTarget?.email}) kalıcı
                  olarak silinecek. Bu işlem <b>geri alınamaz</b>.
                </p>
                <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  Bu kullanıcıya atanmış bekleyen görevler otomatik olarak
                  atamasız kalacak — sonra bulk-reassign ile yeni birine
                  aktarabilirsin.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="user-delete-cancel">
              Vazgeç
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                doDeleteUser();
              }}
              data-testid="user-delete-confirm"
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Kalıcı olarak sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* "Hatırlat" — warm nudge email modal. Preview + edit before send. */}
      <Dialog
        open={!!remindTarget}
        onOpenChange={(o) => !o && !sendingReminder && setRemindTarget(null)}
      >
        <DialogContent
          data-testid="user-remind-dialog"
          className="max-w-2xl"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-violet-600" />
              Hatırlatma gönder
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg border border-violet-100 bg-gradient-to-br from-violet-50 to-white px-3 py-2 text-xs text-violet-900">
              <div className="flex items-center gap-2">
                <UserAvatar
                  name={remindTarget?.name}
                  email={remindTarget?.email}
                  photoUrl={remindTarget?.photo_url}
                  size="sm"
                />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold text-zinc-900">
                    {remindTarget?.name}
                  </div>
                  <div className="truncate text-[11px] text-zinc-500">
                    {remindTarget?.email}
                  </div>
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">
                Konu
              </Label>
              <Input
                value={remindSubject}
                onChange={(e) => setRemindSubject(e.target.value)}
                data-testid="user-remind-subject"
                className="mt-1"
                maxLength={140}
                disabled={sendingReminder}
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">
                Mesaj
              </Label>
              <Textarea
                value={remindMessage}
                onChange={(e) => setRemindMessage(e.target.value)}
                data-testid="user-remind-message"
                className="mt-1 min-h-[180px] font-[13px]"
                disabled={sendingReminder}
              />
              <p className="mt-1 text-[11px] text-zinc-400">
                Markdown desteklenir. Aşağıdaki butonla e-postayı, aynı zamanda
                <span className="mx-1 rounded bg-violet-100 px-1.5 py-0.5 font-mono text-[10px] text-violet-700">Serpix&apos;e Dön</span>
                CTA butonu ile birlikte göndereceğiz.
              </p>
            </div>
          </div>
          <div className="mt-2 flex items-center justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setRemindTarget(null)}
              disabled={sendingReminder}
              data-testid="user-remind-cancel"
            >
              Vazgeç
            </Button>
            <Button
              onClick={sendReminder}
              disabled={sendingReminder}
              data-testid="user-remind-send"
              className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:from-violet-700 hover:to-indigo-700"
            >
              {sendingReminder ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Gönderiliyor…
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Gönder
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function _initialInviteForm() {
  return {
    email: "",
    name: "",
    role: "user",
    can_create_projects: false,
    can_manage_contacts: true,
  };
}


/**
 * SystemLockControl — ultra_admin panic button. Shows a compact pill
 * with the current lock state; clicking opens a confirmation modal
 * where the operator can supply a public-facing reason (shown to
 * everyone attempting to log in). Only mounted for ultra_admin.
 */
function SystemLockControl() {
  const [status, setStatus] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .get("/system/lock")
      .then(({ data }) => alive && setStatus(data))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const locked = !!status?.locked;

  const toggle = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/system/lock", {
        locked: !locked,
        reason: !locked ? reason.trim() : "",
      });
      setStatus(data);
      setOpenDialog(false);
      setReason("");
      toast.success(
        data.locked
          ? "Sistem KİLİTLENDİ — ultra yönetici dışında kimse giremez"
          : "Sistem tekrar açık",
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={() => setOpenDialog(true)}
        data-testid="system-lock-btn"
        className={`gap-2 border ${
          locked
            ? "border-red-300 bg-red-50 text-red-700 hover:bg-red-100"
            : "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
        }`}
      >
        {locked ? (
          <>
            <Lock className="h-4 w-4" />
            Sistem kilitli
          </>
        ) : (
          <>
            <LockOpen className="h-4 w-4" />
            Sistem açık
          </>
        )}
      </Button>
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent data-testid="system-lock-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-red-600" />
              {locked ? "Sistemi tekrar aç" : "Sistemi kullanıma kapat"}
            </DialogTitle>
          </DialogHeader>
          {locked ? (
            <div className="space-y-2 text-sm text-zinc-700">
              <p>
                Şu an sistem <b>kilitli</b>. Ultra yönetici dışında hiç kimse
                giriş yapamıyor.
              </p>
              {status?.reason && (
                <div className="rounded-md bg-zinc-50 px-3 py-2 text-xs text-zinc-600">
                  <span className="font-semibold">Duyuru:</span> {status.reason}
                </div>
              )}
              <p className="text-xs text-zinc-500">
                Kilit kaldırıldığında tüm kullanıcılar tekrar giriş yapabilir.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
                Bu işlem TÜM tenant&apos;larda giriş yapmayı engelleyecek.
                Sadece <b>Ultra Yönetici</b> giriş yapabilecek.
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">
                  Kullanıcıya gösterilecek gerekçe (opsiyonel)
                </Label>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Örn: Planlı bakım · 21:00-22:00"
                  className="mt-1"
                  rows={2}
                  data-testid="system-lock-reason"
                />
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setOpenDialog(false)}
              disabled={saving}
            >
              Vazgeç
            </Button>
            <Button
              onClick={toggle}
              disabled={saving}
              data-testid="system-lock-confirm"
              className={
                locked
                  ? "bg-emerald-600 text-white hover:bg-emerald-700"
                  : "bg-red-600 text-white hover:bg-red-700"
              }
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  İşleniyor…
                </>
              ) : locked ? (
                "Sistemi Aç"
              ) : (
                "Sistemi Kilitle"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
